import { AuthenticatedUser, ISchedule, IUserFull } from "@gettactic/api";
import { dateStringToLocalTime } from "@gettactic/helpers/src/date-time/dateStringToLocalTime";
import { mapWorkplaceRequestToReserveFormData } from "@gettactic/helpers/src/reserve/mapWorkplaceRequestToReserveFormData";
import { ReserveModalDisclosureOptions } from "./types";

export const getDuplicateReservationData = async ({
  item,
  user,
  isWorkplaceRequestsAllowed,
  api,
  shouldSelectResource = false,
  authenticatedUser
}: {
  item: ISchedule | null;
  user?: AuthenticatedUser | IUserFull;
  isWorkplaceRequestsAllowed: boolean;
  api: any;
  shouldSelectResource?: boolean;
  authenticatedUser?: AuthenticatedUser;
}): Promise<ReserveModalDisclosureOptions> => {
  if (!item) {
    return {
      resourceType: "desk",
      office: null,
      floorId: null,
      resource: null
    };
  }
  const attenders =
    item.resource.type === "meeting_room"
      ? item.slots
          .map((slot) => {
            return slot.user;
          })
          ?.filter((u) => u && u.id !== item.creator?.id) || []
      : undefined;
  let workplaceRequestItems: any;
  let workplaceRequestTypes: any;

  if (isWorkplaceRequestsAllowed) {
    try {
      workplaceRequestItems =
        await api.client.workplaceRequests.getWorkplaceRequestsBySchedule(
          item.id
        );
      workplaceRequestTypes =
        await api.client.workplaceRequestTypes.getWorkplaceRequestTypes();
    } catch (error) {
      console.warn("Failed to fetch workplace requests for duplication", error);
    }
  }

  // Calculate duration from original schedule
  // For meeting rooms, use the slot's start/end times instead of main schedule times
  let startTime: Date;
  let endTime: Date;

  if (item.slots.length > 0) {
    // Use the first slot's times for meeting rooms
    startTime = new Date(dateStringToLocalTime(item.slots[0].start));
    endTime = new Date(dateStringToLocalTime(item.slots[0].end));
  } else {
    // Use main schedule times for other resource types
    startTime = new Date(dateStringToLocalTime(item.start));
    endTime = new Date(dateStringToLocalTime(item.end));
  }

  const duration = Math.round(
    (endTime.getTime() - startTime.getTime()) / (1000 * 60)
  ); // duration in minutes

  return {
    title: item.title || undefined,
    resourceType: item.resource.type,
    office: authenticatedUser?.offices?.byId?.[item.resource.office_id] ?? null,
    floorId: item.resource.parent_id,
    resource: shouldSelectResource ? item.resource : null, // Don't pre-select resource to allow office/floor selection unless requested
    start: new Date(
      startTime.getFullYear(),
      startTime.getMonth(),
      startTime.getDate()
    ),
    time: startTime,
    duration: duration,
    workplaceRequestItems: isWorkplaceRequestsAllowed
      ? mapWorkplaceRequestToReserveFormData(
          workplaceRequestItems?.result || [],
          workplaceRequestTypes?.result?.elements || [],
          false
        )
      : undefined,
    attenders,
    owner: ((user as AuthenticatedUser)?.user ?? user) as IUserFull
  };
};
